import { EDUCATORS_ROUTE_SIGNUP, EDUCATORS_ROUTE_TERMS } from '~/constants/routes/educators';
import { useConfigStore } from '~/stores/config';

// Allowlist, not blocklist: guests and anonymous-lesson participants may reach
// only the public landings, the auth surface, the share-link join funnel
// (/start → /onboarding, where a student signs up to join a teacher-shared
// lesson), the emailed invite landing (/accept-invite, where the invite code is
// its own authorization), and the legal pages. Everything else on a schools
// cluster default-denies to login, so new pages are private by default.
// Landings are exact, their subpaths stay gated.
const GUEST_EXACT_PATHS = ['/', '/educators', '/leaders', '/advisors', '/rewards', '/request-a-demo'];

const GUEST_PREFIX_PATHS = [
  '/login',
  '/educators/login',
  EDUCATORS_ROUTE_SIGNUP,
  '/educators/verify-email',
  // Closing an account signs the person out before it opens this page.
  '/educators/account-deleted',
  '/auth',
  '/start',
  '/onboarding',
  '/accept-invite',
  '/terms',
  '/privacy-policy',
  EDUCATORS_ROUTE_TERMS,
];

const isAllowedForGuests = (path: string): boolean =>
  GUEST_EXACT_PATHS.includes(path)
  || GUEST_PREFIX_PATHS.some(prefix => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Schools surface access gate (CP-10279): every content view on a schools
 * cluster must be attributable to a consented, school-linked session, so
 * anonymous browsing prompts login. This is the UX half; the backend
 * EnsureSchoolsClusterAuthenticated middleware is the enforcement. Consumer
 * clusters are untouched. The terms/enrolment funnel for authenticated
 * users stays with 03.cluster-terms-acceptance.
 */
export default defineNuxtRouteMiddleware((to) => {
  if (!useConfigStore().isSchoolsCluster) {
    return;
  }

  // Route middleware re-runs while the error page renders (any 404 this host
  // serves). The error page exposes no content, and a login redirect here would
  // mask every guest-facing error status as a 200 login page.
  if (useError().value) {
    return;
  }

  // An anonymous-lesson participant holds a user and is still held to the guest paths, matching
  // the backend gate, which answers them off a list of their own rather than waving a session
  // through. Without this they would navigate into a portal page and meet its refusal as a failed
  // query instead of a login prompt.
  const { user, isLessonGhost } = useAuth();
  if (user.value && !isLessonGhost.value) {
    return;
  }

  const path = clusterRelativePath(to.path);

  if (isAllowedForGuests(path)) {
    return;
  }

  // Legacy /schools/** is portal traffic until 01.cluster-product-route-protection
  // canonicalises it, so it prompts the educators login too.
  const loginPath = path.startsWith('/educators') || path.startsWith('/schools')
    ? '/educators/login'
    : '/login';

  // This redirect is the highest-volume enforcement point — it fires before the
  // page's data-fetch, so the backend gate never sees normal guest navigation.
  // Emit an info signal (not warn — guest redirects are expected) so an
  // over-gated public path surfaces as a redirect-rate spike in DataDog.
  // `redirectPath`, not `path`: logInfoMessage's global context owns `path`
  // (raw fullPath) and wins the spread, so a `path` key here never ships.
  useNuxtApp().$logInfoMessage('Schools access gate redirected guest to login', {
    redirectPath: path,
    clusterCode: useConfigStore().cluster?.code,
  });

  return navigateTo(clusterRedirectPath(loginPath), { replace: true });
});
